import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormProvider, useController, useForm } from 'react-hook-form';

import {
  type AchPositivePayRuleResponse,
  type CreateAchPositivePayRuleRequest,
  transferAchPositivePayRulesCreateAchPositivePayRuleMutation,
  transferAchPositivePayRulesDeleteAchPositivePayRuleMutation,
  transferAchPositivePayRulesListAchPositivePayRulesByBankAccountQueryKey,
} from '@column/api';
import { CurrenciesUSD } from '@column/column-ui-kit';
import {
  AmountInput,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@column/ui';

import { RenderFields } from '~/components';
import { getErrorMessage } from '~/lib/api/getErrorMessage';
import { useNotificationStore } from '~/stores/Notification';

// Backend accepts exactly 10 chars from [A-Za-z0-9-] and canonicalizes to uppercase.
const ACH_COMPANY_ID_REGEX = /^[A-Za-z0-9-]{10}$/;
const DESCRIPTION_MAX_LENGTH = 255;

type AmountCondition = 'equal' | 'less_than_or_equal_to' | 'any_amount';

const AMOUNT_CONDITIONS: { value: AmountCondition; label: string }[] = [
  { value: 'equal', label: 'Equal to' },
  { value: 'less_than_or_equal_to', label: 'Less than or equal to' },
  { value: 'any_amount', label: 'Any amount' },
];

const usd = CurrenciesUSD[0];
const centsToDollarString = (cents: number): string => (cents / usd.numberToBasic).toString();
const dollarStringToCents = (dollars: string): number => Math.round(Number(dollars) * usd.numberToBasic);

interface RuleForm {
  condition: AmountCondition;
  amount: string;
  description: string;
  achCompanyId: string;
}

const FieldError: FC<{ message?: string }> = ({ message }) =>
  message ? <p className="mt-1.5 text-xs text-destructive">{message}</p> : null;

// "Any amount" collapses the amount field and morphs the borderless condition select into a
// standard full-width box, with a "Condition" label sliding in to replace the hidden amount.
const AmountConditionFields: FC<{ amountRef: React.RefObject<HTMLInputElement | null> }> = ({ amountRef }) => {
  const { field: conditionField } = useController<RuleForm, 'condition'>({ name: 'condition' });
  const { field: amountField, fieldState: amountState } = useController<RuleForm, 'amount'>({
    name: 'amount',
    rules: {
      validate: (value: string, formValues: RuleForm) => {
        if (formValues.condition === 'any_amount') {
          return true;
        }
        const cents = dollarStringToCents(value);
        return (!!value && !Number.isNaN(cents) && cents > 0) || 'Enter an amount greater than 0.';
      },
    },
  });

  const condition = conditionField.value;
  const isAmountDisabled = condition === 'any_amount';
  const conditionLabel = AMOUNT_CONDITIONS.find((option) => option.value === condition)?.label;

  return (
    <div className="-mt-1 flex flex-col gap-0">
      <div
        className={cn(
          'grid transition-all duration-300 ease-out',
          isAmountDisabled ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <Label htmlFor="app-rule-condition" className="pt-4 pb-1.5">
            Condition
          </Label>
        </div>
      </div>

      <Select value={condition} onValueChange={(value) => conditionField.onChange(value as AmountCondition)}>
        <SelectTrigger
          id="app-rule-condition"
          aria-label="Amount condition"
          className={cn(
            'transition-all duration-300 ease-out',
            isAmountDisabled
              ? 'w-full'
              : 'w-fit bg-transparent px-0 text-foreground shadow-none hover:bg-transparent hover:shadow-none data-[state=open]:bg-transparent data-[state=open]:shadow-none dark:bg-transparent dark:shadow-none dark:hover:bg-transparent dark:hover:shadow-none dark:data-[state=open]:bg-transparent dark:data-[state=open]:shadow-none'
          )}
        >
          <SelectValue>{conditionLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {AMOUNT_CONDITIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div
        className={cn(
          'grid transition-all duration-300 ease-out',
          isAmountDisabled ? 'grid-rows-[0fr] -translate-y-1 opacity-0' : 'grid-rows-[1fr] translate-y-0 opacity-100'
        )}
      >
        <div className="overflow-hidden">
          <AmountInput
            id="app-rule-amount"
            aria-label="Amount"
            inputRef={amountRef}
            containerClassName="text-[32px] font-medium leading-tight"
            className="py-1 text-[32px] font-medium leading-tight text-foreground placeholder:text-gray-300 dark:placeholder:text-gray-600"
            prefix={
              !isAmountDisabled && (
                <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">
                  $
                </span>
              )
            }
            value={amountField.value}
            onValueChange={amountField.onChange}
            onBlur={amountField.onBlur}
            placeholder="0.00"
            disabled={isAmountDisabled}
            aria-invalid={!!amountState.error}
          />
          <FieldError message={amountState.error?.message} />
        </div>
      </div>
    </div>
  );
};

export interface ACHPositivePayRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  bankAccountId: string;
  /** When provided, the modal edits this rule (delete + recreate under the hood). */
  rule?: AchPositivePayRuleResponse | null;
  /** When editing, renders a destructive Delete action in place of Cancel. */
  onDelete?: () => void;
}

export const ACHPositivePayRuleModal: FC<ACHPositivePayRuleModalProps> = ({
  isOpen,
  onClose,
  bankAccountId,
  rule,
  onDelete,
}) => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const queryClient = useQueryClient();

  const createRule = useMutation(transferAchPositivePayRulesCreateAchPositivePayRuleMutation());
  const deleteRule = useMutation(transferAchPositivePayRulesDeleteAchPositivePayRuleMutation());

  const isEditing = !!rule;

  const [submitError, setSubmitError] = useState<string | null>(null);
  const amountRef = useRef<HTMLInputElement>(null);
  // Tracks a rule already deleted during an edit so a retry after a failed recreate skips the
  // (now not-found) second delete and goes straight to recreating.
  const deletedRuleIdRef = useRef<string | null>(null);

  const formMethods = useForm<RuleForm>({
    defaultValues: { condition: 'equal', amount: '', description: '', achCompanyId: '' },
  });
  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset({
      condition: (rule?.amount_condition as AmountCondition) ?? 'equal',
      amount: rule && rule.amount_condition !== 'any_amount' ? centsToDollarString(rule.amount) : '',
      description: rule?.description ?? '',
      achCompanyId: rule?.ach_company_id ?? '',
    });
    setSubmitError(null);
    deletedRuleIdRef.current = null;
  }, [isOpen, rule, reset]);

  const isSubmitting = createRule.isPending || deleteRule.isPending;

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: transferAchPositivePayRulesListAchPositivePayRulesByBankAccountQueryKey({
        path: { bank_account_id: bankAccountId },
      }),
    });

  const onValid = async (data: RuleForm) => {
    setSubmitError(null);

    const body: CreateAchPositivePayRuleRequest = {
      bank_account_id: bankAccountId,
      description: data.description.trim(),
      ach_company_id: data.achCompanyId.toUpperCase(),
      amount_condition: data.condition,
      ...(data.condition === 'any_amount' ? {} : { amount: dollarStringToCents(data.amount) }),
    };

    try {
      const alreadyDeletedOriginal = deletedRuleIdRef.current === rule?.ach_positive_pay_rule_id;

      if (!rule) {
        await createRule.mutateAsync({ body });
      } else if (rule.ach_company_id.toUpperCase() !== body.ach_company_id) {
        // Company ID changed: the new key differs, so create the new rule first then remove the old
        // one (no unique collision, no coverage gap).
        await createRule.mutateAsync({ body });
        if (!alreadyDeletedOriginal) {
          try {
            await deleteRule.mutateAsync({ path: { ach_positive_pay_rule_id: rule.ach_positive_pay_rule_id } });
            deletedRuleIdRef.current = rule.ach_positive_pay_rule_id;
          } catch {
            addDangerNotification({
              content:
                'The updated rule was created, but removing the previous rule failed. Please review the rules for duplicates.',
            });
          }
        }
      } else {
        // Same Company ID: the unique key forbids creating first, so delete then recreate. If the
        // recreate fails, coverage is briefly lost — surface a blocking error and keep the modal
        // open to retry (the retry skips the already-done delete).
        if (!alreadyDeletedOriginal) {
          await deleteRule.mutateAsync({ path: { ach_positive_pay_rule_id: rule.ach_positive_pay_rule_id } });
          deletedRuleIdRef.current = rule.ach_positive_pay_rule_id;
        }
        try {
          await createRule.mutateAsync({ body });
        } catch (error) {
          invalidateList();
          setSubmitError(
            `The existing rule was removed but saving the update failed (${getErrorMessage(error)}). Retry to restore coverage.`
          );
          return;
        }
      }

      invalidateList();
      addSuccessNotification({
        content: isEditing ? 'ACH positive pay rule updated' : 'ACH positive pay rule created',
      });
      onClose();
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent
        onOpenAutoFocus={(event) => {
          const input = amountRef.current;
          if (!input || input.disabled) {
            return;
          }
          event.preventDefault();
          input.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit ACH Positive Pay Rule' : 'Create ACH Positive Pay Rule'}</DialogTitle>
          <DialogDescription>
            Allow incoming ACH debits that match this ACH Company ID and amount condition. Everything else is returned.
          </DialogDescription>
        </DialogHeader>

        <FormProvider {...formMethods}>
          {RenderFields<RuleForm>({
            sections: [
              {
                variant: 'isCompact',
                fields: [
                  { content: <AmountConditionFields amountRef={amountRef} /> },
                  {
                    id: 'description',
                    label: 'Description',
                    oneColumn: true,
                    rules: {
                      required: true,
                      maxLength: {
                        value: DESCRIPTION_MAX_LENGTH,
                        message: `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer.`,
                      },
                      validate: (value: string) => value.trim().length > 0 || 'Description is required.',
                    },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <>
                        <Input
                          id="app-rule-description"
                          value={String(value)}
                          onChange={onChange}
                          onBlur={onBlur}
                          maxLength={DESCRIPTION_MAX_LENGTH}
                          placeholder="e.g. Payroll provider"
                          aria-invalid={!!error}
                        />
                        <FieldError message={error?.message} />
                      </>
                    ),
                  },
                  {
                    id: 'achCompanyId',
                    label: 'ACH Company ID',
                    oneColumn: true,
                    rules: {
                      required: true,
                      validate: (value: string) =>
                        ACH_COMPANY_ID_REGEX.test(value) ||
                        'Must be exactly 10 characters (letters, numbers, or hyphens).',
                    },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <>
                        <Input
                          id="app-rule-company-id"
                          value={String(value)}
                          onChange={(event) => onChange(event.target.value.toUpperCase())}
                          onBlur={onBlur}
                          placeholder="10-character identifier"
                          aria-invalid={!!error}
                        />
                        <FieldError message={error?.message} />
                      </>
                    ),
                  },
                ],
              },
            ],
          })}

          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        </FormProvider>

        <DialogFooter className="sm:justify-between">
          {isEditing && onDelete ? (
            <Button variant="danger" size="sm" onClick={onDelete} disabled={isSubmitting}>
              Delete
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button size="sm" onClick={handleSubmit(onValid)} disabled={isSubmitting}>
            {isEditing ? (
              'Save Changes'
            ) : (
              <>
                <Plus />
                Create Rule
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
